import { useFlyOutProductsBasedOnSelectedCloud } from '../../../common/utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { isEmpty } from 'lodash'
import { useFlag } from '@labrav/flags'
import { ColumnMenuOption } from '@labrav/react-components'
import {
  isLoading,
  LoadingTypes,
  searchDropDownOptionMapping,
  searchDropDownOptions,
  updateAppAlert,
  useAccountId,
  useCurrentCloudProvider,
  useScreenSize,
  useValidateCloudRoute,
} from '@labrav/common'

import { useStyles } from './SubscriptionsList.styles'
import { SubscriptionFilterSection } from './SubscriptionFilterSection/SubscriptionFilterSection'
import { ChannelOfferCreationModal } from '../../ChannelOffers/ChannelOfferCreationModal'
import { SubscriptionsDataTable } from '../SubscriptionsDataTable/SubscriptionsDataTable'
import { useDispatch, useSelector } from '../../../store'
import type { RootState } from '../../../store/types'
import {
  clearSubscriptions,
  getSubscriptions,
  setPageNumber,
} from '../../../modules/subscription/action'
import { SubscriptionsState } from '../../../modules/subscription/reducer'
import { ListingCreationPage } from '../../../common/components/ListingCreationPage/ListingCreationPage'
import {
  subscriptionListSortToApiParams,
  subscriptionsRangeStatus,
  type SubscriptionFilter,
  type SubscriptionSortConfig,
} from '../../../utils/subscriptionsFilterMapping'
import { CloudMarketplace } from '../../../modules/productsListing/types'
import { useSyncCloudMarketplace } from '@labrav/common'
import { useSyncFlyoutRouteParams } from '../../../common/utils/hooks/useSyncFlyoutRouteParams'
import { getProducts } from '../../../modules/productsListing/action'

export {
  getSubscriptionIdFromSubscription,
  getExpirationDateFromSubscription,
  getKeyCloudAccountForSubscription,
} from '../../../utils/subscriptionsListHelpers'

export type { SubscriptionFilter }

export const SubscriptionsList = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchNow, setSearchNow] = useState(false)
  const [showCrossMark, setShowCrossMark] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [selectedOption, setSelectedOption] = useState('Company')

  useSyncCloudMarketplace()
  const selectedCloudTypeProductListing = useCurrentCloudProvider()
  const { pageNumber, subscriptions, count } = useSelector<SubscriptionsState>(
    state =>
      state?.subscriptions || { pageNumber: 0, subscriptions: [], count: 0 }
  )

  const privateOfferId = useMemo(
    () => new URLSearchParams(location.search).get('privateOfferId') || '',
    [location.search]
  )

  const loading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.GENERAL)
  )
  const partnershipType = useSelector(
    (state: RootState) =>
      (
        state as RootState & {
          tenants?: { selectedTenant?: { partnershipType?: string[] } }
        }
      ).tenants?.selectedTenant?.partnershipType
  )
  const isPartnerCP = useMemo(
    () => partnershipType?.includes('CONSULTING_PARTNER'),
    [partnershipType]
  )

  const {
    partner_id: accountIdFromParams,
    cloud: routeCloud,
    accountId: routeAccountId,
  } = useParams<{
    partner_id?: string
    cloud?: string
    accountId?: string
  }>()

  const accountIdFromHook = useAccountId({ product: 'flyout' })
  const accountId = useMemo(
    () => routeAccountId || accountIdFromHook || accountIdFromParams || '',
    [routeAccountId, accountIdFromHook, accountIdFromParams]
  )

  useSyncFlyoutRouteParams()
  useValidateCloudRoute('flyout', '/flyout', false)

  const { state } = useLocation()
  const { pageSize: pageSizeValue } = useScreenSize()
  const { products: productRows, selectedProduct: productId } =
    useFlyOutProductsBasedOnSelectedCloud()

  const allAccounts = useSelector(
    (state: RootState) =>
      (state.accountSelection?.availableAccounts || []) as Array<{
        id: string
        product?: string
        cloud?: string
      }>
  )

  useEffect(() => {
    if (routeCloud && routeCloud.toUpperCase() === 'GCP' && routeAccountId) {
      const flyoutAccounts = allAccounts.filter(
        acc => acc.product?.toLowerCase() === 'flyout'
      )
      const accountForRoute = flyoutAccounts.find(
        acc => acc.id === routeAccountId
      )
      if (accountForRoute?.cloud === 'AWS') {
        navigate(`/flyout/aws/${routeAccountId}/subscriptions`, {
          replace: true,
        })
        return
      }
      if (accountForRoute?.cloud === 'AZURE') {
        navigate(`/flyout/azure/${routeAccountId}/subscriptions`, {
          replace: true,
        })
      }
    }
  }, [routeCloud, routeAccountId, navigate, allAccounts])

  const cloudMarketplace: CloudMarketplace = useMemo(
    () =>
      routeCloud
        ? (routeCloud.toUpperCase() as CloudMarketplace)
        : selectedCloudTypeProductListing,
    [routeCloud, selectedCloudTypeProductListing]
  )

  const subscriptionSearchOptions = useMemo(
    () =>
      cloudMarketplace === 'AWS'
        ? searchDropDownOptions.subsciptionsOptions
        : searchDropDownOptions.subsciptionsOptionsMultiCloud,
    [cloudMarketplace]
  )

  useEffect(() => {
    setSelectedOption(cloudMarketplace === 'AWS' ? 'Company' : 'All')
  }, [cloudMarketplace])

  const productsLoading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.FETCH_PRODUCTS)
  )

  const getProductsAndClearSubscriptions = useCallback(async () => {
    await dispatch(clearSubscriptions())
    await dispatch(getProducts(accountId, cloudMarketplace, true, !!productId))
  }, [accountId, cloudMarketplace, productId, dispatch])

  const lastFetchedRef = useRef<{
    accountId: string
    cloudMarketplace: CloudMarketplace
  } | null>(null)

  useEffect(() => {
    const hasAccountChanged =
      lastFetchedRef.current?.accountId !== accountId ||
      lastFetchedRef.current?.cloudMarketplace !== cloudMarketplace

    if (
      !isEmpty(accountId) &&
      !productsLoading &&
      hasAccountChanged &&
      isEmpty(productRows)
    ) {
      lastFetchedRef.current = { accountId, cloudMarketplace }
      getProductsAndClearSubscriptions()
    }
  }, [
    accountId,
    cloudMarketplace,
    productsLoading,
    getProductsAndClearSubscriptions,
    productRows,
  ])

  const { flag: contractStatusUpdate, loading: contractStatusUpdateLoading } =
    useFlag('contractStatusUpdate')
  const isContractStatusUpdateEnabled = !!contractStatusUpdate?.value

  const [selectedSubscriptionsFilter, setSelectedSubscriptionsFilter] =
    useState<subscriptionsRangeStatus | ''>(
      state?.status || subscriptionsRangeStatus.ALL
    )
  const [selectedSubscription, setSelectedSubscription] = useState('')
  const [appliedFilteredValues, setAppliedFilteredValues] =
    useState<SubscriptionFilter>({ status: state?.status || '' })
  const lastAppliedFlagDefault = useRef<boolean | null>(null)

  useEffect(() => {
    if (contractStatusUpdateLoading || state?.status) return
    if (lastAppliedFlagDefault.current === isContractStatusUpdateEnabled) return
    lastAppliedFlagDefault.current = isContractStatusUpdateEnabled

    setSelectedSubscriptionsFilter(
      isContractStatusUpdateEnabled
        ? subscriptionsRangeStatus.ALL
        : subscriptionsRangeStatus.ACTIVE
    )
  }, [
    contractStatusUpdateLoading,
    isContractStatusUpdateEnabled,
    state?.status,
  ])

  const selectedActiveReceivedResellerAuthorization = useSelector<string>(
    (state: RootState) =>
      (
        state as RootState & {
          receivedResellerAuthorization?: {
            selectedActiveReceivedResellerAuthorization?: string
          }
        }
      ).receivedResellerAuthorization
        ?.selectedActiveReceivedResellerAuthorization || ''
  )

  const [subscriptionListSortConfig, setSubscriptionListSortConfig] =
    useState<SubscriptionSortConfig | null>(null)

  const subscriptionListSortParams = useMemo(
    () =>
      subscriptionListSortToApiParams(
        cloudMarketplace === 'GCP' ? subscriptionListSortConfig : undefined
      ),
    [cloudMarketplace, subscriptionListSortConfig]
  )

  useEffect(() => {
    if (cloudMarketplace !== 'GCP') {
      setSubscriptionListSortConfig(null)
    }
  }, [cloudMarketplace])

  const filterStatus = useMemo(
    () => appliedFilteredValues?.status,
    [appliedFilteredValues?.status]
  )
  const filterExpiringDays = useMemo(
    () => appliedFilteredValues?.expiringDays,
    [appliedFilteredValues?.expiringDays]
  )

  const isAnyFilterApplied = useMemo(
    () => !!appliedFilteredValues?.status,
    [appliedFilteredValues?.status]
  )
  const { screenWidth } = useScreenSize()
  const isBelowMedium = useMemo(() => screenWidth <= 1440, [screenWidth])
  const { classes } = useStyles({
    searchNow,
    isAnyFilterApplied: isAnyFilterApplied || isBelowMedium,
  })

  const lastFetchParamsRef = useRef<{
    accountId: string
    productId: string | undefined
    cloudMarketplace: CloudMarketplace
    searchNow: boolean
    inputValue: string
    selectedOption: string
    status: string | undefined
    expiringDays: string | undefined
    privateOfferId: string
    sortConfigKey: string
    sortConfigDirection: string
    contractStatusUpdate: boolean
  } | null>(null)

  const handleSearchClick = useCallback(async () => {
    if (inputValue.trim() !== '') {
      setSearchNow(true)
      setShowCrossMark(true)
      await dispatch(clearSubscriptions())
      lastFetchParamsRef.current = {
        accountId,
        productId,
        cloudMarketplace,
        searchNow: true,
        inputValue,
        selectedOption,
        status: filterStatus,
        expiringDays: filterExpiringDays,
        privateOfferId,
        sortConfigKey: subscriptionListSortConfig?.key ?? '',
        sortConfigDirection: subscriptionListSortConfig?.direction ?? '',
        contractStatusUpdate: isContractStatusUpdateEnabled,
      }
      await dispatch(
        getSubscriptions({
          cloudMarketplace,
          partnerId: accountId,
          productId,
          pageNumber: 1,
          pageSize: pageSizeValue,
          input: inputValue,
          selectedInput: (
            searchDropDownOptionMapping.subsciptionsOptionMapping as Record<
              string,
              string
            >
          )[selectedOption],
          sectionName: 'agreements',
          contractStatusUpdate: isContractStatusUpdateEnabled,
          ...subscriptionListSortParams,
        })
      )
    }
  }, [
    inputValue,
    accountId,
    productId,
    cloudMarketplace,
    pageSizeValue,
    selectedOption,
    filterStatus,
    filterExpiringDays,
    privateOfferId,
    subscriptionListSortParams,
    subscriptionListSortConfig,
    isContractStatusUpdateEnabled,
    dispatch,
  ])

  const mapping = useMemo(
    () => ({
      [cloudMarketplace === 'AWS' || cloudMarketplace === 'GCP'
        ? 'Company'
        : 'Buyer']: 'buyerCompanyName',
      Product: 'productName',
      Offer: 'privateOfferName',
      'Start date': 'contractStartDate',
      'End date': 'contractExpirationDate',
      Status: 'status',
      'Offer type': 'offerType',
      '': 'action',
    }),
    [cloudMarketplace]
  )

  const handleNext = useCallback(
    (subscriptionId?: string) => {
      navigate(
        `/flyout/${routeCloud}/${accountId}/subscriptions/${subscriptionId}/authorization/${selectedActiveReceivedResellerAuthorization}/edit`
      )
    },
    [
      navigate,
      routeCloud,
      accountId,
      selectedActiveReceivedResellerAuthorization,
    ]
  )

  const handleSelectSubscription = useCallback(
    (option: ColumnMenuOption) => {
      if (option?.subscriptionItem?.offerType === 'CPPO') {
        setSelectedSubscription(option?.subscriptionItem?.subscriptionId)
      } else if (option?.route) {
        navigate(option.route)
      }
    },
    [navigate]
  )

  const productRowsWasEmptyRef = useRef(true)

  useEffect(() => {
    if (contractStatusUpdateLoading) return

    const productsAreReady = !isEmpty(productRows) && !productsLoading
    if (productsAreReady && productRowsWasEmptyRef.current) {
      productRowsWasEmptyRef.current = false
      if (!lastFetchParamsRef.current) {
        lastFetchParamsRef.current = null
      }
    } else if (!productsAreReady) {
      productRowsWasEmptyRef.current = true
    }

    if (accountId && productId !== undefined && productsAreReady) {
      const currentParams = {
        accountId,
        productId,
        cloudMarketplace,
        searchNow,
        inputValue,
        selectedOption,
        status: filterStatus,
        expiringDays: filterExpiringDays,
        privateOfferId,
        sortConfigKey: subscriptionListSortConfig?.key ?? '',
        sortConfigDirection: subscriptionListSortConfig?.direction ?? '',
        contractStatusUpdate: isContractStatusUpdateEnabled,
      }

      const lastParams = lastFetchParamsRef.current
      const hasParamsChanged =
        !lastParams ||
        lastParams.accountId !== currentParams.accountId ||
        lastParams.productId !== currentParams.productId ||
        lastParams.cloudMarketplace !== currentParams.cloudMarketplace ||
        lastParams.searchNow !== currentParams.searchNow ||
        lastParams.selectedOption !== currentParams.selectedOption ||
        lastParams.status !== currentParams.status ||
        lastParams.expiringDays !== currentParams.expiringDays ||
        lastParams.privateOfferId !== currentParams.privateOfferId ||
        lastParams.sortConfigKey !== currentParams.sortConfigKey ||
        lastParams.sortConfigDirection !== currentParams.sortConfigDirection ||
        lastParams.contractStatusUpdate !== currentParams.contractStatusUpdate

      if (hasParamsChanged) {
        lastFetchParamsRef.current = currentParams

        if (searchNow && !isEmpty(inputValue) && !isEmpty(selectedOption)) {
          dispatch(
            getSubscriptions({
              cloudMarketplace,
              partnerId: accountId,
              productId,
              pageNumber: 1,
              pageSize: pageSizeValue,
              input: inputValue,
              selectedInput: (
                searchDropDownOptionMapping.subsciptionsOptionMapping as Record<
                  string,
                  string
                >
              )[selectedOption || 'Company'],
              sectionName: 'agreements',
              contractStatusUpdate: isContractStatusUpdateEnabled,
              ...subscriptionListSortParams,
            })
          )
        } else if (!searchNow) {
          dispatch(
            getSubscriptions({
              cloudMarketplace,
              partnerId: accountId,
              pageNumber: 1,
              pageSize: pageSizeValue,
              productId,
              status: filterStatus,
              expiringDays: filterExpiringDays,
              privateOfferId,
              contractStatusUpdate: isContractStatusUpdateEnabled,
              ...subscriptionListSortParams,
            })
          )
        }
      }
    }
  }, [
    searchNow,
    productId,
    filterStatus,
    filterExpiringDays,
    privateOfferId,
    pageSizeValue,
    selectedOption,
    productRows,
    productsLoading,
    subscriptionListSortParams,
    subscriptionListSortConfig,
    accountId,
    cloudMarketplace,
    dispatch,
    inputValue,
    isContractStatusUpdateEnabled,
    contractStatusUpdateLoading,
  ])

  useEffect(() => {
    return () => {
      dispatch(
        updateAppAlert({
          message: undefined,
          messageType: 'INFO',
          autoClose: true,
        })
      )
      dispatch(clearSubscriptions())
      dispatch(setPageNumber(0))
    }
  }, [dispatch])

  const tableHeight = useMemo(
    () =>
      searchNow && count > 0
        ? 'calc(100vh - 240px)'
        : isAnyFilterApplied
          ? 'calc(100vh - 280px)'
          : loading
            ? 'calc(100vh - 296px)'
            : 'calc(100vh - 215px)',
    [searchNow, count, isAnyFilterApplied, loading]
  )

  return (
    <>
      {productRows && !isPartnerCP && !productRows?.length && !loading ? (
        <ListingCreationPage />
      ) : (
        <div className={classes.root}>
          <SubscriptionFilterSection
            options={subscriptionSearchOptions}
            handleSearchClick={handleSearchClick}
            loading={loading}
            searchNow={searchNow}
            setSearchNow={setSearchNow}
            showCrossMark={showCrossMark}
            setShowCrossMark={setShowCrossMark}
            inputValue={inputValue}
            setInputValue={setInputValue}
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
            selectedSubscriptionsFilter={selectedSubscriptionsFilter}
            setSelectedSubscriptionsFilter={setSelectedSubscriptionsFilter}
            appliedFilteredValues={appliedFilteredValues}
            setAppliedFilteredValues={setAppliedFilteredValues}
          />
          <SubscriptionsDataTable
            mapping={mapping}
            count={count}
            pageNumber={pageNumber}
            subscriptions={subscriptions}
            selectedSubscription={selectedSubscription}
            handleSelectSubscription={handleSelectSubscription}
            tableHeight={tableHeight}
            searchNow={searchNow}
            inputValue={inputValue}
            selectedOption={selectedOption}
            appliedFilteredValues={appliedFilteredValues}
            privateOfferId={privateOfferId}
            sortConfig={
              cloudMarketplace === 'GCP' ? subscriptionListSortConfig : null
            }
            onSortConfigChange={setSubscriptionListSortConfig}
          />
        </div>
      )}
      {selectedSubscription && (
        <ChannelOfferCreationModal
          open={selectedSubscription ? 'create' : ''}
          setOpen={setSelectedSubscription}
          handleNext={handleNext}
          subscriptionId={selectedSubscription}
        />
      )}
    </>
  )
}
